import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { FluxService } from '../../../service/FluxService';
import { FormsService } from '../../../service/FormService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { generateFormSessionId } from '../../../util/generateFormSessionId';
import { IFlow, INode, INodeFlow, IPage } from '../../NewFormBuilder/Context/FormBuilderContext';
import {
  ActionTypes,
  IActionType,
  publishedFormReducer,
  PublishedFormState,
} from '../Reducer';

interface PublishedForm {
  id: string;
  name: string;
  publishedNodes: INode[];
  status: 'draft' | 'published';
  publishType: 'internal' | 'external';
  formStyle: {
    backgroundColor?: string;
    backgroundImage?: string;
    pageOrientation?: 'SWAP_PAGE' | 'SIDE_BY_SIDE' | 'PEEKING';
  };
  formSettings: {
    successMessage?: string;
    loadingMessage?: string;
    agentSessionMode?: boolean;
  };
  flows: IFlow[];
  activeFlows: string[];
  pages?: IPage[];
}

interface PublishedFormContextType {
  state: PublishedFormState;
  dispatch: React.Dispatch<IActionType>;
  isLoadingForm: boolean;
  publishedForm: PublishedForm;
  errorType: string;
  publishedNodes: INode[];
  pages: IPage[];
  handleSubmitForm: (redirectPageId?: string) => void;
  isSubmitting: boolean;
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
  redirectPageIdAfterSubmit: string | null;
  clearRedirectPageId: () => void;
}

const PublishedFormContext = createContext<
  PublishedFormContextType | undefined
>(undefined);

interface PublishedFormProviderProps {
  isPreview: boolean;
  children: ReactNode;
}

export function PublishedFormProvider({
  isPreview,
  children,
}: PublishedFormProviderProps) {
  const { formId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [errorType, setErrorType] = useState('');
  const [socketClient, setSocketClient] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formSessionId, setFormSessionId] = useState<string | null>(null);
  const [redirectPageIdAfterSubmit, setRedirectPageIdAfterSubmit] = useState<string | null>(null);
  const [state, dispatch] = useReducer(publishedFormReducer, {
    variablesInputs: [],
    variablesOutputs: [],
  });

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    data: formData,
    isLoading: isLoadingForm,
    error,
  } = useQuery({
    queryKey: ['form-published', formId],
    queryFn: async () => {
      if (isPreview) {
        const response = await FormsService.getForm(formId!);
        return {
          form: response.data,
          nodes: response.data.newFormNodes,
        };
      } else {
        const response = await FormsService.getFormPublished(formId!);
        return {
          form: response.data,
          nodes: response.data.newFormNodesPublished,
        };
      }
    },
    enabled: !!formId,
    refetchOnWindowFocus: !isPreview,
    refetchInterval: !isPreview ? 5 * 60 * 1000 : false,
  });

  useEffect(() => {
    if (!isPreview && formData?.form?.formSettings?.agentSessionMode) {
      const sessionFromUrl = searchParams.get('session');

      if (!sessionFromUrl) {
        const newSessionId = generateFormSessionId();
        setSearchParams({ session: newSessionId });
        setFormSessionId(newSessionId);
      } else {
        setFormSessionId(sessionFromUrl);
      }
    }
  }, [isPreview, formData?.form?.formSettings?.agentSessionMode, searchParams, setSearchParams]);

  useEffect(() => {
    if (formData?.nodes) {
      dispatch({ type: ActionTypes.CLEAR_VARIABLES_INPUTS });
      dispatch({ type: ActionTypes.CLEAR_VARIABLES_OUTPUTS });

      formData.nodes.forEach((node: INode) => {
        if (node.type === 'input' || node.type === 'list') {
          let flowNodeInfo = null;

          for (const flow of formData.form.flows) {
            const foundNode = flow.nodes.find((flowNode: any) =>
              flowNode.id === node.data.varDataInputId
            );

            if (foundNode) {
              flowNodeInfo = {
                id: foundNode.id,
                isExtraction: foundNode.data.isExtraction || false,
                typeOCR: foundNode.data.typeOCR || null,
              };
              break;
            }
          }

          const variableInput = {
            inputId: node.data.varDataInputId as string,
            inputText: '',
            links: [],
            isExtraction: flowNodeInfo?.isExtraction || false,
            typeOCR: flowNodeInfo?.typeOCR || null,
          };

          dispatch({
            type: ActionTypes.CREATE_VARIABLES_INPUTS,
            payload: { variableInput },
          });
        }

        if (node.type === 'output') {
          const variableOutput = {
            outputId: node.data.varDataOutputId as string,
            text: '',
          };

          dispatch({
            type: ActionTypes.CREATE_VARIABLES_OUTPUTS,
            payload: { variableOutput },
          });
        }
      });
    }
  }, [formData?.nodes]);

  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.message;

      if (errorMessage === 'You need to be logged in to access this interface') {
        setErrorType('login');
      } else if (errorMessage === 'Interface not found') {
        setErrorType('notFound');
      } else if (errorMessage === 'Sorry, this interface is not published or its not available') {
        setErrorType('notPublished');
      }
    }
  }, [error]);

  useEffect(() => {
    const socket = io(import.meta.env.VITE_API_URL as string);

    socket.on('connect', () => {
      setSocketClient(socket);
    });

    socket.on('form-test', (response: any) => {
      if (!response.data.success) {
        setIsSubmitting(false);
        setRedirectPageIdAfterSubmit(null);

        setNotification({
          type: 'error',
          message: `Error submitting form: ${response.data.error.message}`,
        });

        dispatch({
          type: ActionTypes.CLEAR_OUTPUTS
        });

        return;
      }

      if (response.data.success && response?.data.data) {
        const result = response.data.data;
        result.forEach((item: { outputId: string; text: string }) => {
          dispatch({
            type: ActionTypes.UPDATE_OUTPUTS,
            payload: { outputId: item.outputId, text: item.text },
          });
        });
      }

      setIsSubmitting(false);
      setNotification({
        type: 'success',
        message:
          formData?.form?.formSettings?.successMessage &&
            formData?.form?.formSettings?.successMessage !== ''
            ? formData.form.formSettings.successMessage
            : 'Interface submitted successfully',
      });
    });

    return () => {
      socket?.off('connect');
      socket?.off('form-test');
    };
  }, [formData?.form]);

  function getVariableInputsByFlowId(flowId: string) {
    if (!formData?.form) return [];

    const flow = formData.form.flows.find((f: IFlow) => f.id === flowId);
    if (!flow) return [];

    const flowNodeIds = flow.nodes.map((node: INodeFlow) => node.id);

    return state.variablesInputs.filter(variableInput =>
      flowNodeIds.includes(variableInput.inputId)
    );
  }

  async function handleSubmitForm(redirectPageId?: string) {
    if (!formData?.form) return;

    if (redirectPageId) {
      setRedirectPageIdAfterSubmit(redirectPageId);
    }

    setIsSubmitting(true);

    try {
      const flowPromises = formData.form.activeFlows.map(async (flowId: string) => {
        const flowVariableInputs = getVariableInputsByFlowId(flowId);

        const requestBody: any = {
          variableInputs: flowVariableInputs,
        };

        if (formData.form.formSettings?.agentSessionMode && formSessionId) {
          requestBody.sessionId = formSessionId;
        }

        return FluxService.formApi(flowId, formData.form.id, socketClient?.id!, requestBody);
      });


      await Promise.all(flowPromises);

    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      setRedirectPageIdAfterSubmit(null);
      setNotification({
        type: 'error',
        message: 'Error submitting form. Please try again.',
      });
    }
  }

  function clearRedirectPageId() {
    setRedirectPageIdAfterSubmit(null);
  }

  return (
    <PublishedFormContext.Provider
      value={{
        state,
        dispatch,
        isLoadingForm,
        publishedForm: formData?.form || ({} as PublishedForm),
        errorType,
        publishedNodes: formData?.nodes || [],
        pages: formData?.form?.pages || [],
        handleSubmitForm,
        isSubmitting,
        isUploading,
        setIsUploading,
        redirectPageIdAfterSubmit,
        clearRedirectPageId,
      }}
    >
      {children}
    </PublishedFormContext.Provider>
  );
}

export function usePublishedForm() {
  const context = useContext(PublishedFormContext);

  if (context === undefined) {
    throw new Error(
      'usePublishedForm must be used within a PublishedFormProvider',
    );
  }
  return context;
}
